import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { APP_URL, LOGO_URL } from "@/i18n/translations";

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/about", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { to: "/contact", label: t.nav.contact },
  ];

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors duration-300 ${
      isActive ? "text-white" : "text-white/55 hover:text-white"
    }`;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#050505]/75 border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <img src={LOGO_URL} alt="Aportta" className="h-7 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.to.slice(1)}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="text-xs font-medium tracking-widest text-white/60 hover:text-white border border-white/15 rounded-full px-3 py-1.5 transition-colors duration-300"
          >
            {lang === "tr" ? "TR / EN" : "EN / TR"}
          </button>
          <a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta"
            className="hidden sm:inline-flex items-center bg-[#FF3B30] hover:bg-[#FF5247] text-white text-sm font-bold px-5 py-2.5 rounded-full transition-all duration-300 hover:shadow-[0_0_24px_rgba(255,59,48,0.5)]"
          >
            {t.nav.cta}
          </a>
          <button
            className="md:hidden text-white p-1"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-[#050505]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="text-white/80 text-lg font-display"
                  data-testid={`mobile-nav-${l.to.slice(1)}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 text-center bg-[#FF3B30] text-white font-bold px-5 py-3 rounded-full"
              >
                {t.nav.cta}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
